import { Layers, Locate, Maximize2, Minus, Plus, Shapes } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { useUiStore, type BaseLayer } from '@/stores/ui-store';
import { BASE_LAYERS } from './map-styles';
import { useMap } from './map-context';

function ControlButton({
  label,
  onClick,
  children,
  className,
}: {
  label: string;
  onClick?: () => void;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          onClick={onClick}
          aria-label={label}
          className={cn('size-9 rounded-none text-muted-foreground hover:text-foreground', className)}
        >
          {children}
        </Button>
      </TooltipTrigger>
      <TooltipContent side="right">{label}</TooltipContent>
    </Tooltip>
  );
}

/**
 * Map chrome lives on the left so it never sits under the vehicle drawer.
 * Grouped into one hairline-divided stack rather than floating buttons.
 */
export function MapControls({ onFitAll }: { onFitAll: () => void }) {
  const { map } = useMap();
  const baseLayer = useUiStore((s) => s.baseLayer);
  const setBaseLayer = useUiStore((s) => s.setBaseLayer);
  const showGeofences = useUiStore((s) => s.showGeofences);
  const setShowGeofences = useUiStore((s) => s.setShowGeofences);
  const showTrails = useUiStore((s) => s.showTrails);
  const setShowTrails = useUiStore((s) => s.setShowTrails);

  return (
    <div className="pointer-events-auto absolute top-4 left-4 z-20 flex flex-col gap-2.5">
      <div className="flex flex-col overflow-hidden rounded-xl bg-card/95 shadow-panel backdrop-blur">
        <ControlButton label="Zoom in" onClick={() => map?.zoomIn({ duration: 220 })}>
          <Plus className="size-4" />
        </ControlButton>
        <div className="h-px bg-border" />
        <ControlButton label="Zoom out" onClick={() => map?.zoomOut({ duration: 220 })}>
          <Minus className="size-4" />
        </ControlButton>
      </div>

      <div className="flex flex-col overflow-hidden rounded-xl bg-card/95 shadow-panel backdrop-blur">
        <ControlButton label="Fit all vehicles" onClick={onFitAll}>
          <Maximize2 className="size-4" />
        </ControlButton>
        <div className="h-px bg-border" />
        <ControlButton
          label="Recentre on my location"
          onClick={() =>
            navigator.geolocation?.getCurrentPosition((position) =>
              map?.flyTo({
                center: [position.coords.longitude, position.coords.latitude],
                zoom: 13,
                duration: 900,
              }),
            )
          }
        >
          <Locate className="size-4" />
        </ControlButton>
      </div>

      <div className="flex flex-col overflow-hidden rounded-xl bg-card/95 shadow-panel backdrop-blur">
        <DropdownMenu>
          <Tooltip>
            <TooltipTrigger asChild>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="Base layer"
                  className="size-9 rounded-none text-muted-foreground hover:text-foreground"
                >
                  <Layers className="size-4" />
                </Button>
              </DropdownMenuTrigger>
            </TooltipTrigger>
            <TooltipContent side="right">Base layer</TooltipContent>
          </Tooltip>

          <DropdownMenuContent side="right" align="start" className="w-56">
            <DropdownMenuLabel className="label-micro">Basemap</DropdownMenuLabel>
            <DropdownMenuRadioGroup
              value={baseLayer}
              onValueChange={(value) => setBaseLayer(value as BaseLayer)}
            >
              {BASE_LAYERS.map((layer) => (
                <DropdownMenuRadioItem key={layer.value} value={layer.value}>
                  <span className="flex flex-col">
                    <span>{layer.label}</span>
                    <span className="text-[10px] text-muted-foreground">{layer.hint}</span>
                  </span>
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>

            <DropdownMenuSeparator />
            <DropdownMenuLabel className="label-micro">Overlays</DropdownMenuLabel>
            <DropdownMenuCheckboxItem
              checked={showGeofences}
              onCheckedChange={(checked) => setShowGeofences(checked === true)}
            >
              Geofences
            </DropdownMenuCheckboxItem>
            <DropdownMenuCheckboxItem
              checked={showTrails}
              onCheckedChange={(checked) => setShowTrails(checked === true)}
            >
              Trail of selected vehicle
            </DropdownMenuCheckboxItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <div className="h-px bg-border" />
        <ControlButton
          label={showGeofences ? 'Hide geofences' : 'Show geofences'}
          onClick={() => setShowGeofences(!showGeofences)}
          className={cn(showGeofences && 'text-primary')}
        >
          <Shapes className="size-4" />
        </ControlButton>
      </div>
    </div>
  );
}
